/**
 * Question To Answer (QtA) block insertion for the docx editor.
 *
 * The QtA module's source data is two strings:
 *   • `summaries_from_doc_sorter` — raw LOI questions extracted by the
 *     additional-comp-extractor-func, e.g. "1.0 Please describe…\n…"
 *   • `experts_response`           — saved per-question answers joined
 *     with "\n\n" (index N answers question N)
 *
 * Render shape (after parsing/stitching):
 *
 *     RESPONSES TO SPECIFIC QUESTIONS
 *
 *     **1.0 Please describe the worker's medical condition...**
 *
 *     [answer paragraph — empty, or the saved answer if one exists]
 *
 *     **2.0 In your opinion is the condition causally related...**
 *
 *     [answer paragraph]
 *
 * Placement: after the last *heading-styled* paragraph in the doc (detected
 * via styleId / outlineLevel attrs from the docx-editor schema). Falls
 * back to "before Yours sincerely/faithfully" when no headings can be
 * detected. Falls back further to end-of-doc.
 *
 * Idempotency: bails the moment any QUESTION_TO_ANSWER_HEADING_TITLE
 * heading is present in the doc. Once inserted, we never re-touch the QtA
 * block — the expert may have edited answers and we don't want to wipe
 * them. Re-running auto-map (or changing LOI files) requires the existing
 * QtA block to be deleted from the doc first.
 */
import { Fragment, type Node as PMNode } from "prosemirror-model";
import type { EditorView } from "prosemirror-view";

import { parseLoiQuestions, stitchAnswers } from "./loi-questions";
import {
  buildInlineNodes,
  firstRunMarks,
  fontMarksOf,
  looksLikeVisualHeading,
  visualParaAttrs,
} from "./para-clone";

export type InsertQtaBlockOptions = {
  /** Heading label to emit (e.g. "RESPONSES TO SPECIFIC QUESTIONS"). */
  headingLabel: string;
  /** Raw LOI questions text — `summaries_from_doc_sorter` on the module. */
  rawQuestions: string;
  /** Saved per-question answers, joined by "\n\n". May be null/empty. */
  expertsResponse?: string | null;
  /** Heading names that, if found anywhere in the doc, mean a QtA block
   *  has already been inserted — used for idempotency. Typically the
   *  QUESTION_TO_ANSWER_HEADING_TITLE constant. */
  existingHeadingNames: readonly string[];
  /** Per-template classification hints from the backend outline service.
   *  When present, drives heading/signature/closing detection in priority
   *  over local heuristics. See `OutlineHints` for shape. Omit to fall
   *  back to pure local detection (e.g. in tests). */
  outlineHints?: OutlineHints;
};

type ParaInfo = {
  node: PMNode;
  text: string;
  from: number;
  to: number;
};

const normalise = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9\s]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const headingTextMatches = (paraText: string, name: string): boolean => {
  const a = normalise(paraText);
  const b = normalise(name);
  if (!a || !b) return false;
  return a === b || a.includes(b) || b.includes(a);
};

/** Pre-classified paragraph text from the backend template outline.
 *  When set, `isHeadingPara` (and the anchor walkers) consult these
 *  before falling back to local style-based heuristics — that's how the
 *  per-template knowledge from the .docx surface gets into the live PM
 *  doc. Sets hold *normalised* paragraph text (see `normaliseForHints`). */
export type OutlineHints = {
  knownHeadings: Set<string>;
  knownChrome: Set<string>;
  knownClosing: Set<string>;
  knownSignature: Set<string>;
};

const normaliseForHints = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9\s]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Build hints from the backend's template-outline response. Bucketing
 *  is by classification; text is normalised once at build time so the
 *  hot-path `isHeadingPara` check is a constant-time Set lookup. */
export const buildOutlineHints = (outline: {
  paragraphs: Array<{ text: string; type: string }>;
}): OutlineHints => {
  const hints: OutlineHints = {
    knownHeadings: new Set<string>(),
    knownChrome: new Set<string>(),
    knownClosing: new Set<string>(),
    knownSignature: new Set<string>(),
  };
  for (const p of outline.paragraphs) {
    const norm = normaliseForHints(p.text);
    if (!norm) continue;
    if (p.type === "heading") hints.knownHeadings.add(norm);
    else if (p.type === "chrome") hints.knownChrome.add(norm);
    else if (p.type === "closing") hints.knownClosing.add(norm);
    else if (p.type === "signature") hints.knownSignature.add(norm);
  }
  return hints;
};

/** Document-chrome patterns — things that LOOK like section headings
 *  (all caps, bold, short) but are doc-title boilerplate, post-nominal
 *  credentials, signature lines, or template placeholders. Profiled
 *  across 68 real Australian medico-legal templates; these are the
 *  highest-frequency false positives for ALLCAPS+BOLD detection.
 *
 *  Mirrors the backend `CHROME_PATTERNS` in `services/template_outline.py`.
 *  When the backend outline is loaded, classification has already been
 *  done server-side — this denylist is the fallback for paragraphs the
 *  outline doesn't cover (our own auto-inserted blocks, etc.). */
const CHROME_PATTERNS: readonly RegExp[] = [
  /^strictly private/i,
  /^confidential medicolegal/i,
  /^independent medical examination/i,
  /^medicolegal report\b/i,
  /^ime report\b/i,
  /^(mbbs|mb\s*bch|ms|md|phd|dphil|franzcp|fracs|frcs|fafoem|fafmm|fafrm|frcpa|grad(?:\s|\.)|dipl?\b)/i,
  /^add\b.*\bhere$/i,
  /^<<.*>>$/,
  /^\(.*\)$/,
  /^(dr|prof(?:essor)?|mr|mrs|ms|mx)\s+[a-z]/i,
];

const isChromeText = (text: string): boolean => {
  const t = text.trim();
  if (!t) return false;
  return CHROME_PATTERNS.some((re) => re.test(t));
};

/** Returns true when a paragraph node looks like a section heading.
 *
 *  Resolution order:
 *    1. Outline hints (when provided): per-template classification
 *       from the backend wins over everything else. If hints say
 *       chrome/closing/signature → NOT a heading. If hints say heading
 *       → IS a heading.
 *    2. Local chrome denylist — skips doc-title / post-nominal / sign-
 *       off / placeholder paragraphs even when the styleId or visual
 *       formatting would otherwise mark them as headings.
 *    3. Word styleId / outlineLevel — deterministic; handles the ~18%
 *       of real templates that use proper Word Heading styles.
 *    4. ALLCAPS + every-run-bold + ≤ 80 chars — visual fallback for
 *       the other ~82% of templates that inline-format their headings.
 *
 *  BOLD mixed-case is intentionally NOT a positive signal — profiling
 *  showed it's dominated by field labels (`ID Sighted:`, `Duration of
 *  Examination:`) and signature names, not section headings. */
export const isHeadingPara = (node: PMNode, hints?: OutlineHints): boolean => {
  const text = node.textContent.trim();
  if (!text) return false;

  if (hints) {
    const norm = normaliseForHints(text);
    if (norm) {
      if (hints.knownChrome.has(norm)) return false;
      if (hints.knownClosing.has(norm)) return false;
      if (hints.knownSignature.has(norm)) return false;
      if (hints.knownHeadings.has(norm)) return true;
    }
  }

  if (isChromeText(text)) return false;

  const attrs = node.attrs as {
    styleId?: string | null;
    outlineLevel?: number | null;
  };
  const styleId = attrs.styleId ?? "";
  if (/^heading\d+$|^title$|^subtitle$/i.test(styleId)) return true;
  const outline = attrs.outlineLevel;
  if (typeof outline === "number" && outline < 9) return true;

  return looksLikeVisualHeading(text, node);
};

const SIGNATURE_RE = /yours\s+(sincerely|faithfully)/i;

const matchesHintBucket = (
  text: string,
  hints: OutlineHints | undefined,
  bucket: keyof OutlineHints
): boolean => {
  if (!hints) return false;
  const norm = normaliseForHints(text);
  if (!norm) return false;
  return hints[bucket].has(norm);
};

/** Phrases that mark a pre-signature wrap-up paragraph in Australian
 *  medico-legal reports — boilerplate that sits between the last section's
 *  body and the signature but doesn't belong to any section. We stop the
 *  body walk on these so our QtA block lands at the end of the last
 *  *section's* content, not after the wrap-up paragraphs.
 *
 *  Conservative patterns — match only unambiguous closing phrases so we
 *  don't accidentally cut off a section body that legitimately starts
 *  with one of these phrases. */
const CLOSING_PHRASE_RE =
  /^(this report (is|was|has been)|should any (aspect|part|further)|if you (have|require|wish|need|would like)|please (do not hesitate|feel free|contact))/i;

/** Position immediately after the last heading-styled paragraph + its
 *  body, capped at the signature line. "Body" = the run of paragraphs
 *  after the last heading that aren't themselves headings, signature
 *  markers, or trailing blanks. Returns null if no heading was found in
 *  the body region.
 *
 *  Why cap at the signature: templates often style their sign-off block
 *  (`DR <NAME>`, `Specialty`, etc.) with the same Heading style used for
 *  section titles. Walking from the END of the doc would grab those as
 *  "the last heading" and drop our QtA block below the signature — which
 *  is exactly the bug we hit on the first cut. Searching only above the
 *  signature gives us the last *section* heading, which is what we want. */
/** Whole-paragraph sign-offs beyond "Yours …". Anchored (^…$) so body text
 *  like "in regards to…" or "…sincerely held opinion" never matches — only a
 *  paragraph that IS the salutation. QA: a template signing off "Kind regards"
 *  wasn't detected, so the QtA block spilled below the signature + credentials
 *  instead of capping above them (it must land after the last section heading,
 *  not after the sign-off). */
const SIGNOFF_RE =
  /^(kind(est)?\s+regards|warm(est)?\s+regards|best\s+(regards|wishes)|regards|sincerely|yours\s+truly)[\s,.]*$/i;

const isSignaturePara = (text: string, hints?: OutlineHints): boolean =>
  matchesHintBucket(text, hints, "knownSignature") ||
  SIGNATURE_RE.test(text) ||
  SIGNOFF_RE.test(text.trim());

const isClosingPara = (text: string, hints?: OutlineHints): boolean =>
  matchesHintBucket(text, hints, "knownClosing") ||
  CLOSING_PHRASE_RE.test(text.trim());

const findAfterLastHeadingAnchor = (
  paragraphs: ParaInfo[],
  hints?: OutlineHints
): number | null => {
  const signatureIdx = paragraphs.findIndex((p) => isSignaturePara(p.text, hints));
  const limit = signatureIdx === -1 ? paragraphs.length : signatureIdx;

  let lastHeadingIdx = -1;
  for (let i = limit - 1; i >= 0; i--) {
    if (isHeadingPara(paragraphs[i].node, hints)) {
      lastHeadingIdx = i;
      break;
    }
  }
  if (lastHeadingIdx === -1) return null;

  // Walk forward from the last heading, still capped by the signature
  // line so we never spill past it. Stops on:
  //   • another heading (small body, next section is closer than signature),
  //   • a closing-boilerplate paragraph ("This report is…", "Should any…")
  //     — these wrap-up paragraphs sit between the section body and the
  //     signature without their own heading, and the user wants the QtA
  //     block to sit BEFORE them, not after.
  let endIdx = lastHeadingIdx;
  for (let j = lastHeadingIdx + 1; j < limit; j++) {
    const p = paragraphs[j];
    if (isHeadingPara(p.node, hints)) break;
    if (isClosingPara(p.text, hints)) break;
    endIdx = j;
  }
  // Drop trailing blanks within the body window so we insert directly
  // after the last *content* paragraph.
  while (endIdx > lastHeadingIdx && paragraphs[endIdx].text.trim().length === 0) {
    endIdx--;
  }
  return paragraphs[endIdx].to;
};

/** Fallback anchor: before the signature paragraph if present. */
const findBeforeSignatureAnchor = (
  paragraphs: ParaInfo[],
  hints?: OutlineHints
): number | null => {
  const idx = paragraphs.findIndex((p) => isSignaturePara(p.text, hints));
  return idx === -1 ? null : paragraphs[idx].from;
};

const collectParagraphs = (view: EditorView): ParaInfo[] => {
  const out: ParaInfo[] = [];
  view.state.doc.descendants((node, pos) => {
    if (node.type.name === "paragraph" || node.type.name === "doc_paragraph") {
      out.push({
        node,
        text: node.textContent,
        from: pos,
        to: pos + node.nodeSize,
      });
      return false;
    }
    return true;
  });
  return out;
};

/** Insert a Question-To-Answer block into the doc. Returns the number of
 *  paragraphs inserted (0 when skipped — a QtA heading is already present, or
 *  no anchor found). When there are no questions, the heading is still emitted
 *  as an empty stub so every report carries a Questions section regardless of
 *  the template or LOI.
 *
 *  Idempotency policy: once *any* QtA-like heading exists in the doc,
 *  this helper bails. We don't try to relocate or refresh — an earlier
 *  iteration of that logic ran into edge cases where the deletion
 *  range silently swallowed adjacent content (signature image, wrap-up
 *  paragraphs) on real docs even though it passed unit tests against a
 *  mocked schema. If a misplaced block needs to move, the safe path is
 *  to delete it from the editor UI by hand, then reload — `insertQtaBlock`
 *  fires fresh against a clean doc. */
export const insertQtaBlock = (
  view: EditorView,
  options: InsertQtaBlockOptions
): number => {
  const questions = parseLoiQuestions(options.rawQuestions);

  const schema = view.state.schema;
  const paragraphType = schema.nodes.paragraph ?? schema.nodes.doc_paragraph;
  const strongMark = schema.marks.strong ?? schema.marks.bold;
  if (!paragraphType) return 0;

  const paragraphs = collectParagraphs(view);
  const hints = options.outlineHints;

  // MDRS-94: many templates ship with an empty QtA stub already in the
  // body — heading like "RESPONSES TO SPECIFIC QUESTION" followed by 0-N
  // blank paragraphs before the next section. The previous idempotency
  // check bailed the moment any QtA-style heading text matched, even when
  // the body span beneath it was empty, leaving the LOI questions
  // unrendered. Disambiguate:
  //   • heading exists AND has any non-blank paragraph below before the
  //     next heading → bail (preserve expert's typed answers — the
  //     original idempotency intent)
  //   • heading exists AND body span is empty → emit question paragraphs
  //     directly under the existing heading; no duplicate heading
  //   • heading absent → fall through to the standard anchor logic
  const existingQtaHeadingIdx = paragraphs.findIndex((p) =>
    options.existingHeadingNames.some((n) => headingTextMatches(p.text, n))
  );
  let fillExistingStub = false;
  if (existingQtaHeadingIdx !== -1) {
    // A QtA heading already exists in the doc. With no questions to add, the
    // heading requirement is already satisfied — leave it untouched.
    if (questions.length === 0) return 0;
    let bodyHasContent = false;
    for (let i = existingQtaHeadingIdx + 1; i < paragraphs.length; i++) {
      if (isHeadingPara(paragraphs[i].node, hints)) break;
      if (paragraphs[i].text.trim().length > 0) {
        bodyHasContent = true;
        break;
      }
    }
    if (bodyHasContent) return 0;
    fillExistingStub = true;
  }

  // Resolve anchor: prefer "after last heading body", fall back to
  // "before signature", fall back to end-of-doc. Outline hints (when
  // present) take priority over local heuristics for classifying which
  // paragraphs count as headings/signatures.
  //
  // When filling an empty stub, anchor at the existing heading's end —
  // the questions slot in right under the existing heading paragraph,
  // displacing the blank stub body.
  const anchorPos = fillExistingStub
    ? paragraphs[existingQtaHeadingIdx].to
    : (findAfterLastHeadingAnchor(paragraphs, hints) ??
      findBeforeSignatureAnchor(paragraphs, hints) ??
      view.state.doc.content.size);

  // Style donors — pick the FIRST heading paragraph in the doc (after
  // any preamble chrome) as the heading donor. Walking from the start
  // gets us the template's "primary" section heading, which carries
  // the cleanest run-mark set (just b/sz/szCs in most templates) and
  // matches the same heading the CoC insertion clones from. Picking
  // the LAST heading was unreliable because later headings sometimes
  // carry additional run attrs (rFonts/color/lang) whose interaction
  // with the editor's renderer collapsed the visible font size.
  //
  // Stays capped at the signature so a styled sign-off block can never
  // be picked as the donor.
  const sigIdx = paragraphs.findIndex((p) => isSignaturePara(p.text, hints));
  const donorLimit = sigIdx === -1 ? paragraphs.length : sigIdx;
  let headingDonor: PMNode | null = null;
  let bodyDonor: PMNode | null = null;
  for (let i = 0; i < donorLimit; i++) {
    if (!isHeadingPara(paragraphs[i].node, hints)) continue;
    headingDonor = paragraphs[i].node;
    bodyDonor = paragraphs[i + 1]?.node ?? null;
    break;
  }

  const headingAttrs = visualParaAttrs(headingDonor);
  const bodyAttrs = visualParaAttrs(bodyDonor);
  const headingRunMarks = headingDonor
    ? firstRunMarks(headingDonor)
    : strongMark
      ? [strongMark.create()]
      : [];
  const bodyRunMarks = bodyDonor ? firstRunMarks(bodyDonor) : [];
  // Questions render as normal BODY text — NOT bold. Keep ONLY the font marks
  // (family/size) from the body donor and drop everything else. An earlier
  // attempt stripped marks named "strong"/"bold", but the vendored editor
  // represents bold with a differently-named mark, so the weight survived and
  // questions still rendered bold (they serialised to a run-level <w:b/>).
  // Keeping only the font subset is name-agnostic: any non-font mark (bold,
  // however it's named) is dropped, so questions keep the template font/size
  // without the weight. Product decision: only the block heading is bold.
  const questionRunMarks: typeof headingRunMarks = fontMarksOf(bodyRunMarks);

  const stitched = stitchAnswers(questions, options.expertsResponse);

  const nodes: PMNode[] = [];
  if (fillExistingStub) {
    // MDRS-94: skip emitting our own heading + leading spacer — the
    // template already provides the heading paragraph. Drop just a
    // single breathing-room spacer between it and the first question.
    nodes.push(paragraphType.create(bodyAttrs, Fragment.empty));
  } else {
    // Leading spacer — gives one blank line between the prior section
    // and our heading so they don't butt up against each other.
    nodes.push(paragraphType.create(bodyAttrs, Fragment.empty));
    // Block heading + breathing spacer beneath it.
    nodes.push(
      paragraphType.create(
        headingAttrs,
        Fragment.from(schema.text(options.headingLabel, headingRunMarks))
      )
    );
    nodes.push(paragraphType.create(bodyAttrs, Fragment.empty));
  }

  for (const { question, answer } of stitched) {
    // Question paragraph at body size, NOT bold — uses the body donor's attrs +
    // marks with bold stripped (see questionRunMarks) so it reads as body text,
    // not a sub-heading.
    const qAttrs = bodyAttrs ? { ...bodyAttrs } : {};
    nodes.push(
      paragraphType.create(
        qAttrs,
        Fragment.from(buildInlineNodes(schema, question, questionRunMarks))
      )
    );
    // Answer paragraph (empty if no saved answer for this index). Use the same
    // font-only marks as the questions: the body donor can carry a stray weight
    // mark (the whole reason questions were bold), so reusing raw bodyRunMarks
    // here would let saved answers render bold too.
    nodes.push(
      paragraphType.create(
        bodyAttrs,
        answer
          ? Fragment.from(buildInlineNodes(schema, answer, questionRunMarks))
          : Fragment.empty
      )
    );
    // Spacer between questions for visual breathing room.
    nodes.push(paragraphType.create(bodyAttrs, Fragment.empty));
  }

  const tr = view.state.tr.insert(anchorPos, Fragment.from(nodes));
  view.dispatch(tr);
  return nodes.length;
};
